import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import type { Json, TablesInsert, TablesUpdate } from "~/api/tables";
import { db } from "~/api/db";
import {
  requireText,
  rootKey,
  workspaceKey,
  textOrNull,
  type ReportView,
  type TaxSetAsideEntry,
} from "./shared";

export function useWorkspaceReportViews(workspaceId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("report-views", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as ReportView[];
      const { data, error } = await db
        .from("report_views")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ReportView[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useMutation({
    mutationFn: async (input: { filters: Json; name: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db.from("report_views").insert({
        workspace_id: workspaceId,
        filters: input.filters,
        name: requireText(input.name, "View name"),
      } satisfies TablesInsert<"report_views">);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("report-views", workspaceId),
      });
    },
  });

  const update = useMutation({
    mutationFn: async (input: { filters: Json; id: string; name: string }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("report_views")
        .update({
          filters: input.filters,
          name: requireText(input.name, "View name"),
        } satisfies TablesUpdate<"report_views">)
        .eq("id", input.id)
        .eq("workspace_id", workspaceId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("report-views", workspaceId),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("report_views")
        .delete()
        .eq("id", id)
        .eq("workspace_id", workspaceId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("report-views", workspaceId),
      });
    },
  });

  return { ...query, create, remove, update, views: query.data ?? [] };
}

export function useWorkspaceTaxSetAsideEntries(
  workspaceId: string | undefined,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: workspaceKey("tax-set-aside", workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [] as TaxSetAsideEntry[];
      const { data, error } = await db
        .from("tax_set_aside_entries")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as TaxSetAsideEntry[];
    },
    enabled: Boolean(workspaceId),
  });

  const create = useMutation({
    mutationFn: async (input: {
      basisCents: number;
      estimateCents: number;
      label: string;
      note?: string;
      percentage: number;
    }) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db.from("tax_set_aside_entries").insert({
        workspace_id: workspaceId,
        basis_cents: Math.max(0, Math.round(input.basisCents)),
        estimate_cents: Math.max(0, Math.round(input.estimateCents)),
        label: requireText(input.label, "Snapshot label"),
        note: textOrNull(input.note),
        percentage: Math.min(100, Math.max(0, input.percentage)),
      } satisfies TablesInsert<"tax_set_aside_entries">);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("tax-set-aside", workspaceId),
      });
    },
  });

  const remove = useMutation({
    mutationFn: async (entryId: string) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const { error } = await db
        .from("tax_set_aside_entries")
        .delete()
        .eq("workspace_id", workspaceId)
        .eq("id", entryId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: workspaceKey("tax-set-aside", workspaceId),
      });
    },
  });

  return { ...query, create, entries: query.data ?? [], remove };
}
